import { NavLink } from "react-router-dom";
import { CalendarCheck, FileSignature, ListTodo, Send, type LucideIcon } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { Card, CardHead, EmptyState } from "../../../ui";
import type { DashAgendaItem } from "../../../../lib/dashboard";

const ICON: Record<DashAgendaItem["kind"], LucideIcon> = {
  task: ListTodo,
  contract: FileSignature,
  campaign: Send,
  ticket: CalendarCheck,
};

const TINT: Record<DashAgendaItem["kind"], string> = {
  task: "var(--color-primary)",
  contract: "var(--color-warning)",
  campaign: "var(--color-success)",
  ticket: "var(--color-primary-2)",
};

const clock = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString("et-EE", { hour: "2-digit", minute: "2-digit" }) : "";

/**
 * Today, in time order.
 *
 * Three different tables land here - tasks due, contracts ending, campaigns
 * starting - because from the operator's side they are one list: things that
 * happen today whether or not anybody opens the page they live on.
 */
export function AgendaCard({ agenda }: { agenda: DashAgendaItem[] }) {
  const t = useT();
  const dayName = new Date().toLocaleDateString("et-EE", { weekday: "long", day: "numeric", month: "long" });

  return (
    <Card>
      <CardHead title={t("dash.agenda.title")} sub={dayName} />
      {agenda.length === 0 ? (
        <EmptyState icon={CalendarCheck} title={t("dash.agenda.emptyTitle")} body={t("dash.agenda.emptyBody")} />
      ) : (
        <div className="crm-agenda">
          {agenda.map((item) => {
            const Icon = ICON[item.kind];
            const tint = TINT[item.kind];
            return (
              <NavLink key={item.key} to={item.to} className="crm-ag">
                <span className="crm-ag-t">{clock(item.at)}</span>
                <span
                  className="crm-ag-i"
                  style={{ background: `color-mix(in oklab, ${tint} 13%, transparent)`, color: tint }}
                >
                  <Icon className="size-4" strokeWidth={1.8} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="crm-ag-n crm-trunc block">{item.title}</span>
                  <span className="crm-ag-s crm-trunc block">{item.sub}</span>
                </span>
              </NavLink>
            );
          })}
        </div>
      )}
    </Card>
  );
}
